import { useState, useEffect, useRef } from 'react';
import { Search, X, Package, Tag, Filter, Warehouse, Users } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { ImageManager } from '@/lib/imageManager';
import { useIsMobile } from '@/hooks/use-mobile';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
} from "@/components/ui/drawer";
import { cn } from '@/lib/utils';
import ProductGradeBadge from '@/components/ProductGradeBadge';

interface StickySearchBarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface SearchResult {
  id: string;
  name: string;
  description: string;
  price_per_unit: number;
  category_name: string;
  type: 'product' | 'category';
  slug?: string;
  images?: string[];
  image_url?: string;
  main_image_index?: number;
  group_buy_enabled?: boolean;
  liquidation_sale_enabled?: boolean;
  grade?: 'A' | 'B' | 'C' | 'D';
}

type FilterType = 'all' | 'regular' | 'liquidation' | 'group';

const SpecialRequestCTA = ({ onClick, variant = 'default' }: { onClick: () => void, variant?: 'default' | 'compact' }) => (
  <div
    className={cn(
      "transition-colors p-4 text-center",
      variant === 'compact' ? "mx-4 mb-2" : "m-4"
    )}
  >
    <div className="flex flex-col items-center gap-3">
      <h4 className="font-medium text-foreground">לא מצאת מה שמחפש?</h4>
      <Button
        onClick={onClick}
        variant="outline"
        size="sm"
        className="text-primary border-primary/20 hover:bg-primary/5 px-6"
      >
        שלח לנו בקשה
      </Button>
    </div>
  </div>
);

// Extracted Content Component to prevent re-renders and focus loss
const SearchContent = ({
  isDrawer,
  query,
  setQuery,
  onClose,
  handleKeyDown,
  loading,
  showResults,
  results,
  filterType,
  setFilterType,
  filteredResults,
  handleResultClick,
  handleSpecialRequest,
  inputRef,
  onQueryChange
}: any) => (
  <div className={cn("flex flex-col h-full", isDrawer ? "p-4" : "container mx-auto px-4 py-4")}>
    <div className="relative space-y-3 flex flex-col h-full">
      {/* Search Input */}
      <div className="relative flex-shrink-0">
        <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-5 w-5 z-10" />
        <Input
          ref={inputRef}
          type="text"
          placeholder="חפש מוצרים, קטגוריות..."
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          onKeyDown={handleKeyDown}
          className="w-full pr-12 pl-12 h-12 text-lg shadow-sm"
        />
        {!isDrawer && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="absolute left-2 top-1/2 transform -translate-y-1/2 h-8 w-8 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Filters - Only show when there are results or loading */}
      {(showResults || loading) && (
        <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-none flex-shrink-0" dir="rtl">
          <Button
            variant={filterType === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterType('all')}
            className="rounded-full text-xs h-7 whitespace-nowrap"
          >
            הכל
          </Button>
          <Button
            variant={filterType === 'regular' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterType('regular')}
            className="rounded-full text-xs h-7 whitespace-nowrap"
          >
            רגיל
          </Button>
          <Button
            variant={filterType === 'liquidation' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterType('liquidation')}
            className="rounded-full text-xs h-7 whitespace-nowrap"
          >
            חיסול
          </Button>
          <Button
            variant={filterType === 'group' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterType('group')}
            className="rounded-full text-xs h-7 whitespace-nowrap"
          >
            קבוצתית
          </Button>
        </div>
      )}

      {/* Initial State - Show CTA immediately if no query - REMOVED per user feedback */}
      {!query && isDrawer && (
        <div className="mt-4 flex-1 flex flex-col items-center justify-center opacity-50">
          <Search className="h-16 w-16 mb-4 text-muted-foreground/30" />
          <p className="text-muted-foreground text-sm">התחל להקליד כדי לחפש...</p>
        </div>
      )}

      {/* Results */}
      {(showResults || loading) && (
        <Card className={cn(
          "bg-card",
          isDrawer ? "flex-1 overflow-y-auto border-0 shadow-none mt-2" : "absolute left-0 right-0 top-full shadow-lg border z-50 mt-2 max-h-96 overflow-y-auto"
        )}>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-4 text-center text-muted-foreground">מחפש...</div>
            ) : filteredResults.length > 0 ? (
              <div className="space-y-1">
                {filteredResults.map((result: any) => (
                  <div
                    key={`${result.type}-${result.id}`}
                    onClick={() => handleResultClick(result)}
                    className="p-4 hover:bg-muted cursor-pointer transition-colors border-b last:border-b-0"
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex flex-col items-center gap-1.5 flex-shrink-0">
                        <div className="relative w-24 h-24 rounded-lg overflow-hidden bg-muted">
                          {result.type === 'product' && result.images && result.images.length > 0 ? (
                            <img
                              src={result.images[result.main_image_index || 0]}
                              alt={result.name}
                              className="w-full h-full object-contain"
                              onError={(e) => { e.currentTarget.src = '/placeholder.svg'; }}
                            />
                          ) : result.type === 'category' && result.image_url ? (
                            <img
                              src={ImageManager.getImageUrl(result.image_url, 'category')}
                              alt={result.name}
                              className="w-full h-full object-contain"
                            />
                          ) : (
                            <div className="bg-primary/10 w-full h-full flex items-center justify-center">
                              {result.type === 'category' ? <Tag className="h-8 w-8 text-primary" /> : <Package className="h-8 w-8 text-primary" />}
                            </div>
                          )}

                          {result.type === 'product' && result.grade && (
                            <div className="absolute top-1 right-1 z-10 shadow-sm rounded-md bg-white/90 backdrop-blur-sm p-0.5">
                              <ProductGradeBadge grade={result.grade} size="sm" />
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex-1 text-right">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-medium text-foreground text-sm">{result.name}</h4>
                          <Badge variant="secondary" className="text-[10px] px-1 h-5">
                            {result.type === 'category' ? 'קטגוריה' : 'מוצר'}
                          </Badge>
                        </div>

                        {result.type === 'product' && (
                          <div className="flex mb-1.5 flex-wrap gap-1.5">
                            <Badge className={cn(
                              "text-[10px] text-white font-bold py-0 h-5 border-0 shadow-sm flex items-center gap-1",
                              result.group_buy_enabled
                                ? "bg-gradient-to-r from-amber-500 to-yellow-400"
                                : result.liquidation_sale_enabled
                                  ? "bg-gradient-to-r from-red-600 to-red-500"
                                  : "bg-gradient-to-r from-blue-600 to-blue-400"
                            )}>
                              {result.group_buy_enabled ? (
                                <><Users className="w-3 h-3" /> קבוצת רכישה</>
                              ) : result.liquidation_sale_enabled ? (
                                <><Warehouse className="w-3 h-3" /> מכירת חיסול</>
                              ) : (
                                <><Package className="w-3 h-3" /> מכירה רגילה</>
                              )}
                            </Badge>
                          </div>
                        )}

                        {result.description && (
                          <p className="text-xs text-muted-foreground line-clamp-1 mb-1">
                            {result.description}
                          </p>
                        )}
                        {result.type === 'product' && (
                          <div className="flex items-center justify-between mt-1">
                            <div className="flex items-center gap-2">
                              {result.price_per_unit > 0 && (
                                <span className="text-sm font-bold text-primary">
                                  ₪{result.price_per_unit}
                                </span>
                              )}
                            </div>
                            {result.category_name && (
                              <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                                {result.category_name}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                {/* Append CTA to end of results */}
                <SpecialRequestCTA onClick={handleSpecialRequest} variant="compact" />
              </div>
            ) : (
              <div className="p-8 text-center bg-transparent">
                {query.trim().length >= 2 && (
                  <div className="flex flex-col items-center">
                    <Search className="h-16 w-16 text-muted-foreground mb-4" />
                    <h3 className="text-xl font-medium text-foreground mb-2">לא נמצאו תוצאות</h3>
                    <p className="text-muted-foreground mb-6">נסה לשנות את הסינון או מילות החיפוש</p>
                    <Button
                      onClick={handleSpecialRequest}
                      variant="outline"
                      className="text-primary border-primary/20 hover:bg-primary/5 px-8"
                    >
                      שלח לנו בקשה
                    </Button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  </div>
);

const StickySearchBar = ({ isOpen, onClose }: StickySearchBarProps) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [filterType, setFilterType] = useState<FilterType>('all');
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Immediate clear if query is too short
    if (query.trim().length < 2) {
      setResults([]);
      setShowResults(false);
      setLoading(false);
      return;
    }

    const searchProducts = async () => {
      setLoading(true);
      try {
        const { data: allProducts, error: productsError } = await (supabase as any)
          .rpc('get_public_product_catalog', {
            limit_count: 1000,
            offset_count: 0
          });

        const products = allProducts?.filter((product: any) =>
          product.name?.toLowerCase().includes(query.toLowerCase()) ||
          product.description?.toLowerCase().includes(query.toLowerCase())
        ).slice(0, 20);

        let productsWithCategories: any[] = [];
        if (products && products.length > 0) {
          const categoryIds = products.map((p: any) => p.category_id).filter(Boolean);
          const { data: categoriesData } = await (supabase as any)
            .from('categories')
            .select('id, name')
            .in('id', categoryIds);

          productsWithCategories = products.map((product: any) => ({
            ...product,
            category_name: categoriesData?.find((cat: any) => cat.id === product.category_id)?.name || ''
          }));
        }

        const { data: categories, error: categoriesError } = await (supabase as any)
          .from('categories')
          .select('id, name, description, image_url')
          .ilike('name', `%${query}%`)
          .limit(3);

        if (productsError || categoriesError) {
          console.error('Search error:', productsError || categoriesError);
          return;
        }

        const searchResults: SearchResult[] = [
          ...(categories || []).map((cat: any) => ({
            id: cat.id,
            name: cat.name,
            description: cat.description || '',
            price_per_unit: 0,
            category_name: '',
            type: 'category' as const,
            image_url: cat.image_url
          })),
          ...(productsWithCategories || []).map((product: any) => ({
            id: product.id,
            name: product.name,
            description: product.description || '',
            price_per_unit: product.price_per_unit || 0,
            category_name: product.category_name || '',
            slug: product.slug,
            images: product.images || [],
            main_image_index: product.main_image_index || 0,
            type: 'product' as const,
            group_buy_enabled: product.group_buy_enabled,
            liquidation_sale_enabled: product.liquidation_sale_enabled,
            grade: product.grade
          }))
        ];

        setResults(searchResults);
        setShowResults(true);
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setLoading(false);
      }
    };

    // Keep debounce
    const debounceTimer = setTimeout(searchProducts, 800);
    return () => clearTimeout(debounceTimer);
  }, [query]);

  // Handle outside click and scroll for desktop
  useEffect(() => {
    if (!isOpen || isMobile) return;

    const handleScroll = () => {
      if (window.innerWidth >= 1024) onClose();
    };

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    const timer = setTimeout(() => {
      window.addEventListener('scroll', handleScroll, { passive: true });
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, isMobile]);

  const handleResultClick = (result: SearchResult) => {
    if (result.type === 'category') {
      navigate(`/category/${encodeURIComponent(result.name)}`);
    } else {
      const productIdentifier = result.slug || result.id;
      navigate(`/product/${productIdentifier}`);
    }
    onClose();
    setQuery('');
    setShowResults(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  };

  const getFilteredResults = () => {
    if (filterType === 'all') return results;
    return results.filter(item => {
      if (item.type === 'category') return true;
      if (filterType === 'group') return item.group_buy_enabled;
      if (filterType === 'liquidation') return item.liquidation_sale_enabled;
      if (filterType === 'regular') return !item.group_buy_enabled && !item.liquidation_sale_enabled;
      return true;
    });
  };

  // Navigates to Contact page -> Finding Product tab
  const handleSpecialRequest = () => {
    onClose();
    navigate('/contact?tab=special-search');
  };

  const handleQueryChange = (val: string) => {
    setQuery(val);
    if (val.trim().length >= 2) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  };

  const filteredResults = getFilteredResults();

  if (isMobile) {
    return (
      <Drawer open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DrawerContent className="h-[90vh] flex flex-col mt-24 max-h-[96vh]">
          <DrawerHeader className="hidden">
            <DrawerTitle>חיפוש</DrawerTitle>
            <DrawerDescription>חיפוש מוצרים</DrawerDescription>
          </DrawerHeader>
          <SearchContent
            isDrawer={true}
            query={query}
            setQuery={setQuery}
            onClose={onClose}
            handleKeyDown={handleKeyDown}
            loading={loading}
            showResults={showResults}
            results={results}
            filterType={filterType}
            setFilterType={setFilterType}
            filteredResults={filteredResults}
            handleResultClick={handleResultClick}
            handleSpecialRequest={handleSpecialRequest}
            inputRef={inputRef}
            onQueryChange={handleQueryChange}
          />
        </DrawerContent>
      </Drawer>
    );
  }

  // Desktop render
  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 top-16 bg-black/60 z-40 transition-opacity duration-200 fade-in"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={containerRef}
        className="fixed z-[100] lg:z-50 lg:inset-auto lg:left-0 lg:right-0 lg:top-16 lg:bottom-auto border-b border-border shadow-md bg-background animate-in slide-in-from-top-2 duration-200"
      >
        <SearchContent
          isDrawer={false}
          query={query}
          setQuery={setQuery}
          onClose={onClose}
          handleKeyDown={handleKeyDown}
          loading={loading}
          showResults={showResults}
          results={results}
          filterType={filterType}
          setFilterType={setFilterType}
          filteredResults={filteredResults}
          handleResultClick={handleResultClick}
          handleSpecialRequest={handleSpecialRequest}
          inputRef={inputRef}
          onQueryChange={handleQueryChange}
        />
      </div>
    </>
  );
};

export default StickySearchBar;